import { Kart, Race, RaceEvent, STEP, raceTime } from "./sim";
import { halfAt, loopDelta, nearestCut } from "./tracks";

// Where on the lap a player's time goes, saved with each run
// (`kart_runs.summary.sectors`) so the lodge's real races make a heatmap:
// supabase/queries/kart_sector_heatmap.sql. TRACK-IMPROVEMENTS.md, TOOL-3.

/** Sectors a lap: each 5% of it. */
export const SECTORS = 20;

export interface Sectors {
  /** When the player first reached each sector, counted on across laps: lap × SECTORS + sector. */
  at: number[];
  /** Seconds against a wall in each sector, all laps together. */
  wall: number[];
  /** Twirls: the sector, and what did it. */
  spins: [number, string][];
  /** Drift boosts: where on the lap they were let go (to 0.01), and their tier. */
  drifts: [number, number][];
  /** Shortcuts taken: which (its index in the track's), and whether it came out at the far end. */
  cuts: [number, boolean][];
  /** The shortcut the player's in now, or -1. */
  cut: number;
}

export const newSectors = (): Sectors => ({ at: [], wall: Array(SECTORS).fill(0), spins: [], drifts: [], cuts: [], cut: -1 });

const sectorOf = (race: Race, kart: Kart) => Math.min(SECTORS - 1, Math.floor((kart.idx / race.track.n) * SECTORS));

/** After each sim step: which sector the player's reached, whether they're on a wall, and in or out of a shortcut. */
export function follow(s: Sectors, race: Race, player: Kart) {
  if (race.phase !== "racing" || player.finishTime !== null) return;
  const { track } = race;
  const reached = player.lap * SECTORS + sectorOf(race, player);
  while (reached >= 0 && s.at.length <= reached) s.at.push(+raceTime(race).toFixed(2));
  const smp = track.samples[player.idx];
  const lat = Math.abs((player.x - smp.x) * smp.nx + (player.z - smp.z) * smp.nz);
  if (lat > halfAt(track, player.idx) - 1 && lat <= halfAt(track, player.idx)) s.wall[sectorOf(race, player)] += STEP;
  const near = lat > halfAt(track, player.idx) ? nearestCut(track, player.x, player.z) : null;
  const cut = near && near.dist < near.half + 1 ? track.cuts.indexOf(near.cut) : -1;
  if (cut === s.cut) return;
  if (s.cut >= 0) {
    // Out of it: at the far end, or back where it started.
    const c = track.cuts[s.cut];
    s.cuts.push([s.cut, Math.abs(loopDelta(player.idx, (c.a + c.skip) % track.n, track.n)) < 12]);
  }
  s.cut = cut;
}

/** The player's twirls and drift boosts, from the race's events. */
export function note(s: Sectors, race: Race, player: Kart, e: RaceEvent) {
  if (e.kart !== player.id) return;
  if (e.kind === "hit" && e.spin) s.spins.push([sectorOf(race, player), e.what]);
  else if (e.kind === "drift") s.drifts.push([+(player.idx / race.track.n).toFixed(2), e.tier]);
}

/** As saved: each lap's sector times, the last closed by the finish if there was one, and the rest. */
export function saved(s: Sectors, finish: number | null) {
  const at = finish === null ? s.at : [...s.at, finish];
  const times = s.at.map((t, k) => +((at[k + 1] ?? NaN) - t).toFixed(2));
  const laps: number[][] = [];
  for (let k = 0; k < times.length; k += SECTORS) laps.push(times.slice(k, k + SECTORS).filter((t) => !Number.isNaN(t)));
  return { laps, wall: s.wall.map((w) => +w.toFixed(2)), spins: s.spins, drifts: s.drifts, cuts: s.cuts };
}
